import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './ProductImageGallery.css';

const ProductImageGallery = ({ mainImage, additionalImages = [], productName, imageApiUrl }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [loading, setLoading] = useState(true);
    const mainImageRef = useRef(null);

    const defaultImage = "/assets/images/logo/logo.png";

    // Tüm görselleri birleştir - useMemo ile memoize et
    const allImages = useMemo(() => {
        return [
            mainImage,
            ...(additionalImages || [])
        ].filter(img => img); // Boş değerleri filtrele
    }, [mainImage, additionalImages]);

    // Tam görsel URL'sini oluştur
    const getFullImageUrl = useCallback((imageUrl) => {
        if (!imageUrl) return defaultImage;
        if (imageUrl.startsWith('http')) return imageUrl;
        return `${imageApiUrl}${imageUrl}`;
    }, [imageApiUrl]);

    // Görsel yükleme durumu - sadece currentImageIndex değiştiğinde çalışır
    useEffect(() => {
        if (!allImages || allImages.length === 0) return;
        
        setLoading(true);
        const img = new Image();
        img.src = getFullImageUrl(allImages[currentImageIndex]);
        img.onload = () => setLoading(false);
        img.onerror = () => setLoading(false);
        
        // Cleanup function
        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [currentImageIndex, allImages, getFullImageUrl]);

    // Bir sonraki görsele geç
    const nextImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    }, [allImages.length]);

    // Bir önceki görsele geç
    const prevImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    }, [allImages.length]);

    // Thumbnail'e tıklama
    const goToImage = useCallback((index) => {
        setCurrentImageIndex(index);
    }, []);

    // Touch events
    const handleTouchStart = useCallback((e) => {
        setTouchStart(e.targetTouches[0].clientX);
    }, []);

    const handleTouchMove = useCallback((e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextImage();
        } else if (isRightSwipe) {
            prevImage();
        }

        setTouchStart(0);
        setTouchEnd(0);
    }, [touchStart, touchEnd, nextImage, prevImage]);

    // Zoom toggle
    const toggleZoom = useCallback(() => {
        setIsZoomed((prev) => !prev);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'ArrowLeft') prevImage();
            if (e.key === 'ArrowRight') nextImage();
            if (e.key === 'Escape') setIsZoomed(false);
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [prevImage, nextImage]);

    // Ürün değiştiğinde index'i sıfırla
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [mainImage]);

    // Eğer görsel yoksa
    if (!allImages || allImages.length === 0) {
        return (
            <div className="product-image-gallery">
                <div className="gallery-main-container">
                    <div className="gallery-main-image">
                        <img 
                            src={defaultImage} 
                            alt={productName || 'Ürün'}
                            className="main-img"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="product-image-gallery">
            {/* Ana Görsel Container */}
            <div className="gallery-main-container">
                {/* Loading spinner */}
                {loading && (
                    <div className="gallery-loading">
                        <div className="spinner">
                            <div className="spinner-ring"></div>
                            <div className="spinner-ring"></div>
                            <div className="spinner-ring"></div>
                            <div className="spinner-ring"></div>
                        </div>
                    </div>
                )}

                {/* Ana Görsel */}
                <div 
                    className={`gallery-main-image ${isZoomed ? 'zoomed' : ''}`}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onClick={toggleZoom}
                    ref={mainImageRef}
                >
                    <img 
                        src={getFullImageUrl(allImages[currentImageIndex])}
                        alt={`${productName} - ${currentImageIndex + 1}`}
                        className={`main-img ${loading ? 'loading' : ''}`}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultImage;
                        }}
                    />
                    
                    {/* Zoom İkonu */}
                    {!isZoomed && (
                        <div className="zoom-hint">
                            <i className="ph ph-magnifying-glass-plus"></i>
                            <span>Yakınlaştırmak için tıklayın</span>
                        </div>
                    )}

                    {/* Görsel Sayacı */}
                    {allImages.length > 1 && (
                        <div className="image-counter">
                            <span>{currentImageIndex + 1} / {allImages.length}</span>
                        </div>
                    )}
                </div>

                {/* Navigation Okları */}
                {allImages.length > 1 && (
                    <>
                        <button 
                            className="gallery-nav-btn prev"
                            onClick={(e) => {
                                e.stopPropagation();
                                prevImage();
                            }}
                            aria-label="Önceki görsel"
                        >
                            <i className="ph ph-caret-left"></i>
                        </button>
                        <button 
                            className="gallery-nav-btn next"
                            onClick={(e) => {
                                e.stopPropagation();
                                nextImage();
                            }}
                            aria-label="Sonraki görsel"
                        >
                            <i className="ph ph-caret-right"></i>
                        </button>
                    </>
                )}
            </div>

            {/* Thumbnail Galeri */}
            {allImages.length > 1 && (
                <div className="gallery-thumbnails">
                    <div className="thumbnails-wrapper">
                        {allImages.map((image, index) => (
                            <div
                                key={index}
                                className={`thumbnail-item ${currentImageIndex === index ? 'active' : ''}`}
                                onClick={() => goToImage(index)}
                            >
                                <img
                                    src={getFullImageUrl(image)}
                                    alt={`${productName} - Thumbnail ${index + 1}`}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = defaultImage;
                                    }}
                                />
                                {currentImageIndex === index && (
                                    <div className="active-indicator"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Zoom Modal */}
            {isZoomed && (
                <div className="zoom-modal" onClick={toggleZoom}>
                    <button className="zoom-close" aria-label="Kapat">
                        <i className="ph ph-x"></i>
                    </button>
                    <img 
                        src={getFullImageUrl(allImages[currentImageIndex])}
                        alt={`${productName} - Zoom`}
                        className="zoom-img"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <div className="zoom-controls">
                        <button 
                            className="zoom-nav-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                prevImage();
                            }}
                        >
                            <i className="ph ph-caret-left"></i>
                        </button>
                        <span className="zoom-counter">
                            {currentImageIndex + 1} / {allImages.length}
                        </span>
                        <button 
                            className="zoom-nav-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                nextImage();
                            }}
                        >
                            <i className="ph ph-caret-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductImageGallery;

